import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Select from "react-select";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { axiosInstance } from "../../../utils/networking";
import { UserContext } from "../../../context/UserContext";
import {
  BulkCloseResult,
  BulkCloseUndoResult,
  BulkReassignResult,
  ManagedCaseIdsResponse,
  ManagedCaseRow,
  ManagedCasesResponse,
  ManagedUserStatus,
} from "../../../interfaces";
import { downloadCsv } from "../../Case/activityExport";
import BatchHistory from "./BatchHistory";
import CloseCasesDialog from "./CloseCasesDialog";
import ReassignCasesDialog from "./ReassignCasesDialog";
import {
  AssignedOption,
  AssignedOptionGroup,
  CaseManageFilters,
  DEFAULT_FILTERS,
  EXPORT_ROW_CAP,
  INACTIVE_DAY_PRESETS,
  MAX_INACTIVE_DAYS,
  YOUNG_CASE_DAYS,
  buildAssignedOptions,
  buildCasesCsv,
  buildCloseReason,
  buildManageQuery,
  casesCsvFilename,
  clampInactiveDays,
  closeEligibleIds,
  describeFilters,
  fetchAllFilterRows,
  filtersFromParams,
  filtersToParams,
  plural,
  sumWarnings,
} from "./caseManagementUtils";

const PRIMARY = "#9E1F63FF";
const PRIMARY_HOVER = "#7d184e";
const CARD_BORDER = "1px solid #ececf0";
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 200];
const DEBOUNCE_MS = 350;
const COLUMN_COUNT = 8;

const buttonSx = { textTransform: "none", fontWeight: 600, borderRadius: 2 } as const;

const AdminsOnly = () => (
  <Box sx={{ padding: "20px", textAlign: "left" }}>
    <Paper
      elevation={0}
      sx={{ border: CARD_BORDER, borderRadius: "16px", textAlign: "center", py: 8 }}
    >
      <Typography variant="h6" sx={{ color: "#8a8a93" }}>
        Admins only
      </Typography>
      <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.5 }}>
        Case Management is available to administrators.
      </Typography>
    </Paper>
  </Box>
);

const StatusBadge = ({ status }: { status: ManagedUserStatus | null | undefined }) => {
  if (status !== "inactive" && status !== "missing") return null;
  const missing = status === "missing";
  return (
    <Chip
      size="small"
      label={missing ? "Missing" : "Inactive"}
      sx={{
        ml: 0.75,
        height: 20,
        fontSize: 11,
        fontWeight: 700,
        backgroundColor: missing ? "#fdecea" : "#fbf5e6",
        color: missing ? "#c62828" : "#8a6d00",
      }}
    />
  );
};

const daysInactiveCell = (row: ManagedCaseRow) => (
  <Chip
    size="small"
    label={
      row.daysInactive === null || row.daysInactive === undefined
        ? "Never"
        : `${row.daysInactive.toLocaleString()} day${row.daysInactive === 1 ? "" : "s"}`
    }
    sx={{
      fontWeight: 700,
      backgroundColor: row.daysInactive === null ? "#fdecea" : "#fbf5e6",
      color: row.daysInactive === null ? "#c62828" : "#8a6d00",
    }}
  />
);

const warningChip = (key: string, label: string, bg: string, fg: string, title: string) => (
  <Tooltip key={key} title={title}>
    <Chip
      size="small"
      label={label}
      sx={{ height: 22, fontSize: 12, fontWeight: 600, backgroundColor: bg, color: fg }}
    />
  </Tooltip>
);

const warningsCell = (row: ManagedCaseRow) => {
  const w = row.warnings || ({} as ManagedCaseRow["warnings"]);
  const chips = [];
  if (w.pendingReminders > 0) {
    chips.push(
      warningChip(
        "rem",
        plural(w.pendingReminders, "reminder"),
        "#fff4e5",
        "#b26a00",
        "Pending reminders on this case",
      ),
    );
  }
  if (w.openReferrals > 0) {
    chips.push(
      warningChip(
        "ref",
        plural(w.openReferrals, "referral"),
        "#e8f4fd",
        "#0b6aa2",
        "Referrals not yet completed or unsuccessful",
      ),
    );
  }
  if (w.openTasks > 0) {
    chips.push(
      warningChip(
        "task",
        plural(w.openTasks, "task"),
        "#f3e8f9",
        "#6b2a8c",
        "Incomplete Action Item tasks",
      ),
    );
  }
  if (w.youngCase) {
    chips.push(
      warningChip(
        "young",
        `<${YOUNG_CASE_DAYS}d`,
        "#fdecea",
        "#c62828",
        `Opened under ${YOUNG_CASE_DAYS} days ago — never bulk-closed`,
      ),
    );
  }
  if (!chips.length) return <Typography sx={{ color: "#b0b0b8" }}>—</Typography>;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
      {chips}
    </Stack>
  );
};

const formatDate = (iso: string | null | undefined) =>
  iso ? moment(iso).format("MMM D, YYYY") : null;

// Admin-only bulk close / reassign of stale cases over GET /cases/manage.
// The server 403 is the real guard; non-admins never reach the page.
const CaseManagement = () => {
  const { user } = useContext(UserContext);
  if (user?.role !== "admin") return <AdminsOnly />;
  return <CaseManagementPage />;
};

const CaseManagementPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // The filters live in the URL (bookmarkable). Debounced inputs fire after
  // their render, so read the LATEST params / setter through refs to avoid
  // writing a stale filter back over a newer one.
  const paramsRef = useRef(searchParams);
  paramsRef.current = searchParams;
  const setParamsRef = useRef(setSearchParams);
  setParamsRef.current = setSearchParams;
  const updateFilters = (patch: Partial<CaseManageFilters>) => {
    const current = filtersFromParams(paramsRef.current);
    setParamsRef.current(filtersToParams({ ...current, ...patch }, paramsRef.current), {
      replace: true,
    });
  };

  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const filterKey = buildManageQuery(filters);

  // --- Inputs with local text state, committed to the URL debounced ---------
  const [search, setSearch] = useState(filters.q);
  const [daysText, setDaysText] = useState(String(filters.inactiveDays));

  useEffect(() => {
    const handle = setTimeout(() => {
      const q = search.trim();
      if (q !== filtersFromParams(paramsRef.current).q) updateFilters({ q });
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    const handle = setTimeout(() => {
      const days = clampInactiveDays(daysText);
      if (days === null) return;
      const current = filtersFromParams(paramsRef.current);
      if (days !== current.inactiveDays) updateFilters({ inactiveDays: days });
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysText]);

  // Back/forward (or a pasted URL) moves the inputs too.
  useEffect(() => {
    setSearch((s) => (s.trim() === filters.q ? s : filters.q));
  }, [filters.q]);
  useEffect(() => {
    setDaysText((t) =>
      clampInactiveDays(t) === filters.inactiveDays ? t : String(filters.inactiveDays),
    );
  }, [filters.inactiveDays]);

  // --- Table data -------------------------------------------------------------
  const [pageState, setPageState] = useState({ key: filterKey, page: 0 });
  // A filter change restarts at page 0 without a wasted request.
  const page = pageState.key === filterKey ? pageState.page : 0;
  const setPage = (p: number) => setPageState({ key: filterKey, page: p });
  const [pageSize, setPageSize] = useState(50);

  const [rows, setRows] = useState<ManagedCaseRow[]>([]);
  const [total, setTotal] = useState(0);
  const [openTotal, setOpenTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [batchesExpandKey, setBatchesExpandKey] = useState(0);
  const [notice, setNotice] = useState<{ severity: "success" | "info" | "error"; text: string } | null>(
    null,
  );

  // Rows seen under the current filter, by id — warnings / young-case flags
  // for selected cases that are not on the current page.
  const [rowCache, setRowCache] = useState<Record<string, ManagedCaseRow>>({});
  const cacheRows = (list: ManagedCaseRow[]) =>
    setRowCache((prev) => {
      const next = { ...prev };
      list.forEach((r) => {
        next[r.case_id] = r;
      });
      return next;
    });

  // --- Selection -----------------------------------------------------------------
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [selectAllInfo, setSelectAllInfo] = useState<{ total: number; truncated: boolean } | null>(
    null,
  );
  const [selectingAll, setSelectingAll] = useState(false);

  const clearSelection = () => {
    setSelected(new Set());
    setSelectAllInfo(null);
  };

  // A selection belongs to the filter that produced it.
  const filterKeyRef = useRef(filterKey);
  filterKeyRef.current = filterKey;
  useEffect(() => {
    clearSelection();
    setRowCache({});
  }, [filterKey]);

  // Monotonic request id: a slow earlier response never overwrites a newer one.
  const requestIdRef = useRef(0);
  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    axiosInstance
      .get(`/cases/manage?${buildManageQuery(filters, { page, pageSize })}`)
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        const data: ManagedCasesResponse = res.data || {};
        const list = Array.isArray(data.rows) ? data.rows : [];
        const matched = typeof data.total === "number" ? data.total : 0;
        // A bulk write can shrink the result under the current page — step
        // back to the last page that still exists.
        if (!list.length && page > 0 && matched > 0) {
          setPage(Math.max(0, Math.ceil(matched / pageSize) - 1));
          return;
        }
        setRows(list);
        setTotal(matched);
        setOpenTotal(typeof data.openTotal === "number" ? data.openTotal : null);
        cacheRows(list);
        setError(null);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        if (err?.response?.status === 403) {
          setForbidden(true);
          return;
        }
        console.error("Failed to load cases:", err);
        setRows([]);
        setTotal(0);
        setError(err?.response?.data?.message || "Could not load cases. Please try again.");
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
      });
    // `filters` is fully described by `filterKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, page, pageSize, refreshKey]);

  // --- Users (Assigned-to filter + successor picker) ---------------------------
  const [users, setUsers] = useState<any[] | null>(null);
  useEffect(() => {
    axiosInstance
      .get(`/user/allUsers`)
      .then((res) => setUsers(Array.isArray(res.data?.users) ? res.data.users : []))
      .catch((err) => {
        console.error("Failed to load users:", err);
        setUsers([]);
      });
  }, []);

  const assignedGroups = useMemo(() => buildAssignedOptions(users || []), [users]);
  const optionByValue = useMemo(() => {
    const map: Record<string, AssignedOption> = {};
    assignedGroups.forEach((g) => g.options.forEach((o) => (map[o.value] = o)));
    return map;
  }, [assignedGroups]);
  const assignedValue = filters.assigned.map(
    (v) =>
      optionByValue[v] || {
        value: v,
        label: users === null ? "Loading…" : "Unknown user",
        kind: "active" as const,
      },
  );

  // --- Derived selection maths -------------------------------------------------
  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const closeIds = useMemo(() => closeEligibleIds(selectedIds, rowCache), [selectedIds, rowCache]);
  const youngExcluded = selectedIds.length - closeIds.length;

  const pageIds = rows.map((r) => r.case_id);
  const pageSelectedCount = pageIds.filter((id) => selected.has(id)).length;
  const allPageSelected = pageIds.length > 0 && pageSelectedCount === pageIds.length;

  const toggleRow = (id: string) => {
    setSelectAllInfo(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const togglePage = () => {
    setSelectAllInfo(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const selectAllMatching = () => {
    const key = filterKey;
    setSelectingAll(true);
    axiosInstance
      .get(`/cases/manage/ids?${buildManageQuery(filters)}`)
      .then((res) => {
        if (filterKeyRef.current !== key) return;
        const data: ManagedCaseIdsResponse = res.data || {};
        const ids = Array.isArray(data.case_ids) ? data.case_ids : [];
        setSelected(new Set(ids));
        setSelectAllInfo({
          total: typeof data.total === "number" ? data.total : ids.length,
          truncated: !!data.truncated,
        });
      })
      .catch((err) => {
        if (err?.response?.status === 403) {
          setForbidden(true);
          return;
        }
        setNotice({ severity: "error", text: "Could not select every matching case." });
      })
      .finally(() => setSelectingAll(false));
  };

  // --- Whole-result loads (CSV export, warnings for the close dialog) ---------
  const [exporting, setExporting] = useState<string | null>(null);
  const [loadingWarnings, setLoadingWarnings] = useState(false);

  const loadAllRows = async (onProgress?: (loaded: number, total: number) => void) => {
    const key = filterKey;
    const out = await fetchAllFilterRows(
      filters,
      (query) => axiosInstance.get(`/cases/manage?${query}`).then((r) => r.data),
      onProgress,
    );
    if (filterKeyRef.current === key) cacheRows(out.rows);
    return out;
  };

  const exportCsv = async (scope: "selection" | "all") => {
    setNotice(null);
    setExporting("Preparing…");
    try {
      let list: ManagedCaseRow[];
      let note = "";
      const cached = selectedIds.map((id) => rowCache[id]).filter(Boolean);
      if (scope === "selection" && cached.length === selectedIds.length) {
        list = cached;
      } else {
        const out = await loadAllRows((loaded, of) =>
          setExporting(`Loading ${loaded.toLocaleString()} of ${of.toLocaleString()}…`),
        );
        list = scope === "selection" ? out.rows.filter((r) => selected.has(r.case_id)) : out.rows;
        if (scope === "all" && out.truncated) {
          note = ` (the first ${EXPORT_ROW_CAP.toLocaleString()} of ${out.total.toLocaleString()})`;
        }
        if (scope === "selection" && list.length < selectedIds.length) {
          note = ` (${(selectedIds.length - list.length).toLocaleString()} selected cases no longer match the filter)`;
        }
      }
      downloadCsv(
        buildCasesCsv(list, window.location.origin),
        casesCsvFilename(scope === "selection" ? "selected" : "all"),
      );
      setNotice({ severity: "success", text: `Exported ${plural(list.length, "case")}${note}.` });
    } catch (err: any) {
      if (err?.response?.status === 403) setForbidden(true);
      else setNotice({ severity: "error", text: "Export failed. Please try again." });
    } finally {
      setExporting(null);
    }
  };

  const loadAllWarnings = async () => {
    setLoadingWarnings(true);
    try {
      await loadAllRows();
    } catch (err: any) {
      if (err?.response?.status === 403) setForbidden(true);
    } finally {
      setLoadingWarnings(false);
    }
  };

  // --- Writes ----------------------------------------------------------------------
  const [closeOpen, setCloseOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);

  const afterWrite = () => {
    clearSelection();
    setRowCache({});
    setRefreshKey((k) => k + 1);
  };

  const onCloseFinished = (_result: BulkCloseResult) => {
    afterWrite();
    setBatchesExpandKey((k) => k + 1);
  };
  const onReassignFinished = (_result: BulkReassignResult) => afterWrite();
  const onUndone = (result: BulkCloseUndoResult) => {
    setNotice({
      severity: "success",
      text: `Reopened ${plural(result.reopened || 0, "case")}${
        result.skipped ? `; ${result.skipped.toLocaleString()} skipped (not closed any more)` : ""
      }. Cancelled reminders were not restored.`,
    });
    setRowCache({});
    setRefreshKey((k) => k + 1);
  };

  const closeWarnings = useMemo(
    () => (closeOpen ? sumWarnings(selectedIds, rowCache) : sumWarnings([], {})),
    [closeOpen, selectedIds, rowCache],
  );

  const defaultReason = useMemo(
    () => buildCloseReason(filters, (id) => optionByValue[id]?.label),
    [filters, optionByValue],
  );

  if (forbidden) return <AdminsOnly />;

  const isDefaultFilters = filterKey === buildManageQuery(DEFAULT_FILTERS);
  const neverOnly = filters.never === "only";
  const daysInvalid = clampInactiveDays(daysText) === null;

  const emptyText = error
    ? "No results to show."
    : filters.q
      ? `No matching cases for "${filters.q}".`
      : "No open cases match these filters.";

  const chipSx = (active: boolean) => ({
    fontWeight: 600,
    cursor: "pointer",
    ...(active
      ? { backgroundColor: PRIMARY, color: "white", "&:hover": { backgroundColor: PRIMARY_HOVER } }
      : { backgroundColor: "#f3f3f6", color: "#2c2c34" }),
  });

  return (
    <Box sx={{ padding: "20px", textAlign: "left" }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: "#2c2c34" }}>
        Case Management
      </Typography>
      <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.5, mb: 2 }}>
        {describeFilters(filters)} Close or reassign them in bulk.
      </Typography>

      <BatchHistory
        refreshKey={refreshKey}
        expandKey={batchesExpandKey}
        onUndone={onUndone}
        onForbidden={() => setForbidden(true)}
      />

      {/* Filters */}
      <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px", p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap", mb: 2 }}>
          <Box sx={{ flex: "1 1 340px", minWidth: 260 }}>
            <Typography variant="caption" sx={{ color: "#8a8a93", fontWeight: 600 }}>
              Assigned to
            </Typography>
            <Select<AssignedOption, true, AssignedOptionGroup>
              isMulti
              closeMenuOnSelect={false}
              options={assignedGroups}
              value={assignedValue}
              onChange={(vals) => updateFilters({ assigned: (vals || []).map((v) => v.value) })}
              placeholder="Anyone"
              isLoading={users === null}
              menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
              formatOptionLabel={(o) => (
                <span>
                  {o.label}
                  {o.kind === "inactive" ? (
                    <span
                      style={{
                        marginLeft: 6,
                        padding: "0 6px",
                        borderRadius: 8,
                        fontSize: 11,
                        fontWeight: 700,
                        backgroundColor: "#fbf5e6",
                        color: "#8a6d00",
                      }}
                    >
                      Inactive
                    </span>
                  ) : null}
                </span>
              )}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 1500 }),
                control: (base) => ({ ...base, minHeight: 40 }),
                multiValue: (base, { data }) => ({
                  ...base,
                  backgroundColor: data.kind === "shortcut" ? "rgba(158,31,99,0.10)" : base.backgroundColor,
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? "#F3F4F6" : "white",
                  color: "black",
                }),
              }}
            />
          </Box>
          <Box sx={{ flex: "0 1 280px", minWidth: 220 }}>
            <Typography variant="caption" sx={{ color: "#8a8a93", fontWeight: 600 }}>
              Firm
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder="Search firm name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
        </Stack>

        <Stack direction="row" spacing={2} useFlexGap alignItems="center" sx={{ flexWrap: "wrap" }}>
          <Stack direction="row" spacing={1} useFlexGap alignItems="center" sx={{ flexWrap: "wrap" }}>
            <Typography variant="body2" sx={{ color: "#2c2c34", fontWeight: 600 }}>
              Inactive for at least
            </Typography>
            <TextField
              size="small"
              type="number"
              value={daysText}
              onChange={(e) => {
                setDaysText(e.target.value);
                // Typing a day count leaves the "Never touched" view.
                if (neverOnly) updateFilters({ never: "include" });
              }}
              error={!neverOnly && daysInvalid}
              inputProps={{ min: 1, max: MAX_INACTIVE_DAYS, "aria-label": "Inactive days" }}
              InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
              sx={{ width: 130, opacity: neverOnly ? 0.6 : 1 }}
            />
            {INACTIVE_DAY_PRESETS.map((d) => (
              <Chip
                key={d}
                size="small"
                label={`${d}+`}
                onClick={() => {
                  setDaysText(String(d));
                  updateFilters({ inactiveDays: d, never: "include" });
                }}
                sx={chipSx(!neverOnly && filters.inactiveDays === d)}
              />
            ))}
            <Tooltip title="Cases with no activity ever recorded, any age">
              <Chip
                size="small"
                label="Never touched"
                onClick={() => updateFilters({ never: neverOnly ? "include" : "only" })}
                sx={chipSx(neverOnly)}
              />
            </Tooltip>
          </Stack>

          <ToggleButtonGroup
            size="small"
            exclusive
            value={filters.basis}
            onChange={(_e, v) => v && updateFilters({ basis: v })}
            sx={{
              "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 600, px: 1.5 },
              "& .Mui-selected": {
                backgroundColor: "rgba(158,31,99,0.10) !important",
                color: `${PRIMARY} !important`,
              },
            }}
          >
            <ToggleButton value="any">Any activity</ToggleButton>
            <Tooltip title="Only interaction activities (contact, follow-ups, assessments…) count">
              <ToggleButton value="interaction">Interactions only</ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>

          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Checkbox
                size="small"
                checked={filters.secondary === "none_active"}
                onChange={(e) =>
                  updateFilters({ secondary: e.target.checked ? "none_active" : "any" })
                }
                sx={{ "&.Mui-checked": { color: PRIMARY } }}
              />
            }
            label={<Typography variant="body2">Exclude cases with an active secondary BFC</Typography>}
          />

          {!isDefaultFilters ? (
            <Button
              size="small"
              onClick={() => {
                setSearch("");
                setDaysText(String(DEFAULT_FILTERS.inactiveDays));
                setParamsRef.current(filtersToParams(DEFAULT_FILTERS, paramsRef.current), {
                  replace: true,
                });
              }}
              sx={{ ...buttonSx, color: PRIMARY }}
            >
              Reset filters
            </Button>
          ) : null}
        </Stack>
      </Paper>

      {/* Count line */}
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        alignItems="center"
        sx={{ mb: 1.5, flexWrap: "wrap" }}
      >
        <Typography variant="body2" sx={{ color: "#8a8a93" }}>
          {loading
            ? "Loading…"
            : `${plural(total, "matching case")}${
                openTotal !== null ? ` of ${openTotal.toLocaleString()} open` : ""
              }`}
        </Typography>
        {total > 0 && !(selectAllInfo && !selectAllInfo.truncated) ? (
          <Button
            size="small"
            onClick={selectAllMatching}
            disabled={selectingAll || loading}
            startIcon={selectingAll ? <CircularProgress size={14} color="inherit" /> : undefined}
            sx={{ ...buttonSx, color: PRIMARY }}
          >
            Select all {total.toLocaleString()} matching
          </Button>
        ) : null}
        <Button
          size="small"
          onClick={() => exportCsv("all")}
          disabled={!!exporting || loading || total === 0}
          startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
          sx={{ ...buttonSx, color: "#2c2c34" }}
        >
          Export all to CSV
        </Button>
        {exporting ? (
          <Typography variant="body2" sx={{ color: "#8a8a93" }}>
            {exporting}
          </Typography>
        ) : null}
      </Stack>

      {selectAllInfo ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {selectAllInfo.truncated
            ? `The first ${selected.size.toLocaleString()} of ${selectAllInfo.total.toLocaleString()} matching cases are selected (selection limit). Narrow the filters to reach the rest.`
            : `All ${selected.size.toLocaleString()} matching cases are selected.`}{" "}
          <Button size="small" onClick={clearSelection} sx={{ ...buttonSx, color: PRIMARY, py: 0 }}>
            Clear selection
          </Button>
        </Alert>
      ) : null}

      {notice ? (
        <Alert severity={notice.severity} sx={{ mb: 1.5 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      ) : null}

      <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px", overflow: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { fontWeight: 700, color: "#2c2c34", whiteSpace: "nowrap" } }}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allPageSelected}
                  indeterminate={pageSelectedCount > 0 && !allPageSelected}
                  disabled={loading || rows.length === 0}
                  onChange={togglePage}
                  inputProps={{ "aria-label": "Select this page" }}
                  sx={{ "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: PRIMARY } }}
                />
              </TableCell>
              <TableCell>Firm</TableCell>
              <TableCell>Assigned to</TableCell>
              <TableCell>Secondary</TableCell>
              <TableCell>Last activity</TableCell>
              <TableCell sortDirection={filters.order}>
                <TableSortLabel
                  active
                  direction={filters.order}
                  onClick={() => updateFilters({ order: filters.order === "desc" ? "asc" : "desc" })}
                >
                  Days inactive
                </TableSortLabel>
              </TableCell>
              <TableCell>Opened</TableCell>
              <TableCell>Warnings</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} sx={{ textAlign: "center", py: 6 }}>
                  <CircularProgress sx={{ color: PRIMARY }} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} sx={{ textAlign: "center", color: "#8a8a93", py: 5 }}>
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const isSelected = selected.has(row.case_id);
                const young = !!row.warnings?.youngCase;
                const checkbox = (
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleRow(row.case_id)}
                    inputProps={{ "aria-label": `Select ${row.firm_name || "case"}` }}
                    sx={{ "&.Mui-checked": { color: young ? "#c62828" : PRIMARY } }}
                  />
                );
                return (
                  <TableRow
                    key={row.case_id}
                    hover
                    selected={isSelected}
                    onClick={() => toggleRow(row.case_id)}
                    sx={{
                      cursor: "pointer",
                      "&.Mui-selected, &.Mui-selected:hover": {
                        backgroundColor: "rgba(158,31,99,0.06)",
                      },
                    }}
                  >
                    <TableCell padding="checkbox">
                      {young ? (
                        <Tooltip
                          title={`Opened under ${YOUNG_CASE_DAYS} days ago — can be reassigned, but bulk close skips it`}
                        >
                          <span>{checkbox}</span>
                        </Tooltip>
                      ) : (
                        checkbox
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <a
                        href={`/case?id=${row.case_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="thrive-user-text"
                        style={{ color: PRIMARY, fontWeight: 600, textDecoration: "none" }}
                      >
                        {row.firm_name || "Unknown"}
                      </a>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {row.owner_name || "—"}
                      <StatusBadge status={row.owner_status} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {row.secondary_name || "—"}
                      <StatusBadge status={row.secondary_status} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDate(row.lastActivity) || (
                        <Typography component="span" variant="body2" sx={{ color: "#8a8a93" }}>
                          No activity recorded
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{daysInactiveCell(row)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(row.caseOpened) || "—"}</TableCell>
                    <TableCell>{warningsCell(row)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      </Paper>

      {/* Sticky action footer */}
      {selected.size > 0 ? (
        <Paper
          elevation={6}
          sx={{
            position: "sticky",
            bottom: 12,
            zIndex: 10,
            mt: 2,
            px: 2,
            py: 1.25,
            borderRadius: "12px",
            border: CARD_BORDER,
          }}
        >
          <Stack direction="row" spacing={1.5} useFlexGap alignItems="center" sx={{ flexWrap: "wrap" }}>
            <Typography sx={{ fontWeight: 700, color: "#2c2c34", mr: "auto" }}>
              {plural(selected.size, "case")} selected
              {youngExcluded > 0 ? (
                <Typography component="span" variant="body2" sx={{ color: "#8a8a93", ml: 1 }}>
                  ({youngExcluded.toLocaleString()} opened &lt;{YOUNG_CASE_DAYS}d — reassign only)
                </Typography>
              ) : null}
            </Typography>
            <Button size="small" onClick={clearSelection} sx={{ ...buttonSx, color: "#2c2c34" }}>
              Clear
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => exportCsv("selection")}
              disabled={!!exporting}
              startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
              sx={{ ...buttonSx, color: "#2c2c34", borderColor: "divider" }}
            >
              Export CSV
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setReassignOpen(true)}
              sx={{ ...buttonSx, color: PRIMARY, borderColor: PRIMARY }}
            >
              Reassign selected ({selected.size.toLocaleString()})
            </Button>
            <Tooltip
              title={
                closeIds.length === 0
                  ? `Every selected case was opened under ${YOUNG_CASE_DAYS} days ago`
                  : ""
              }
            >
              <span>
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  color="error"
                  disabled={closeIds.length === 0}
                  onClick={() => setCloseOpen(true)}
                  sx={buttonSx}
                >
                  Close selected ({closeIds.length.toLocaleString()})
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Paper>
      ) : null}

      <CloseCasesDialog
        open={closeOpen}
        caseIds={closeIds}
        youngExcluded={youngExcluded}
        warnings={closeWarnings}
        onLoadAllWarnings={loadAllWarnings}
        loadingWarnings={loadingWarnings}
        defaultReason={defaultReason}
        onClose={() => setCloseOpen(false)}
        onFinished={onCloseFinished}
        onForbidden={() => setForbidden(true)}
      />
      <ReassignCasesDialog
        open={reassignOpen}
        caseIds={selectedIds}
        users={users}
        onClose={() => setReassignOpen(false)}
        onFinished={onReassignFinished}
        onForbidden={() => setForbidden(true)}
      />
    </Box>
  );
};

export default CaseManagement;
